import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const category = article ? getCategoryBySlug(article.category) : undefined;
  const relatedArticles = article ? getRelatedArticles(article, 3) : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [location.pathname]);

  if (!article) {
    return <NotFound />;
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    articleBody: article.content.replace(/<[^>]*>/g, ''),
    wordCount: article.content.replace(/<[^>]*>/g, '').length,
  };

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`/article/${slug}`}
        ogType="article"
        ogImage={article.image}
        articlePublishedTime={article.date}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <Navbar />
      <main className="pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                ...(category ? [{ label: category.name, path: `/category/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />
          </div>

          <div className="py-8">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 bg-accent-100 text-accent-700 rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}>
                {article.title}
              </h1>

              <div className="flex items-center gap-3 text-sm text-foreground-500 mb-6">
                <span className="font-medium text-foreground-700">{article.author}</span>
                <span>·</span>
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime} 分钟阅读</span>
              </div>
            </div>

            <div className="relative h-[400px] md:h-[500px] rounded-xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
                title={article.title}
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {category && (
              <div className="mt-10 pt-6 border-t border-background-200/60">
                <Link
                  to={`/category/${category.slug}`}
                  className="inline-flex items-center gap-1 text-sm text-accent-600 hover:text-accent-700 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回 {category.name}
                </Link>
              </div>
            )}
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 border-t border-background-200/60">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6"
              style={{ fontFamily: 'var(--font-heading)' }}>
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.slug}`}
                  className="group bg-background-50 border border-background-200/60 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:border-accent-300/50"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={related.image}
                      alt={related.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      title={related.title}
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-accent-600 transition-colors"
                      style={{ fontFamily: 'var(--font-heading)' }}>
                      {related.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-2">
                      {related.author} · {related.date}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}